import { useEffect, useState } from "react";
import { Button, Modal, Radio, Select, Spin } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  fetchProtocolTemplateDropdown,
  type ProtocolTemplateDropdownItem,
} from "../../../pages/protocols/protocolsService";
import { useProtocolsRouter } from "../../custom-hooks/useProtocolsRouter";
import { formatTranslation } from "../../utils/translationUtils";
import "./CreateProtocolModal.less";

export type CreateProtocolOption = "newProtocol" | "template";

interface CreateProtocolModalProps {
  open: boolean;
  onCancel: () => void;
  onContinue: (
    option: CreateProtocolOption,
    templateId?: string,
    templateName?: string
  ) => void;
}

export const CreateProtocolModal = ({
  open,
  onCancel,
  onContinue,
}: CreateProtocolModalProps): JSX.Element => {
  const { t } = useTranslation();
  const { orgId, workspaceId } = useProtocolsRouter();
  const [selectedOption, setSelectedOption] =
    useState<CreateProtocolOption | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [templates, setTemplates] = useState<ProtocolTemplateDropdownItem[]>(
    []
  );
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Fetch templates when modal opens
      void fetchTemplatesData();
    } else {
      // Reset state when modal closes
      setSelectedOption(null);
      setSelectedTemplateId(null);
      setTemplates([]);
      setTemplateError(null);
      setIsLoadingTemplates(false);
    }
  }, [open]);

  const fetchTemplatesData = async (): Promise<void> => {
    if (!orgId || !workspaceId) {
      return;
    }

    setIsLoadingTemplates(true);
    setTemplateError(null);

    try {
      const templatesData = await fetchProtocolTemplateDropdown(
        orgId,
        workspaceId
      );

      setTemplates(templatesData);
    } catch (error) {
      console.error("Error fetching templates:", error);
      setTemplateError(
        formatTranslation(t("createProtocolModal.failedToLoadTemplates"))
      );
      setTemplates([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const isConfirmDisabled = (): boolean => {
    if (!selectedOption) {
      return true;
    }

    if (selectedOption === "template" && !selectedTemplateId) {
      return true;
    }

    return false;
  };

  const renderTemplateContent = (): JSX.Element => {
    if (isLoadingTemplates) {
      return (
        <Spin
          size="small"
          className="create-protocol-modal__template-loading"
        />
      );
    }

    if (templateError) {
      return (
        <div className="create-protocol-modal__template-error">
          {templateError}
        </div>
      );
    }

    return (
      <Select
        placeholder={formatTranslation(t("createProtocolModal.selectTemplate"))}
        value={selectedTemplateId}
        onChange={(value: string) => {
          setSelectedTemplateId(value);
        }}
        options={templates.map((template) => ({
          label: template.name,
          value: template.id,
        }))}
        className="create-protocol-modal__template-dropdown"
      />
    );
  };

  // No `isConfirmDisabled()` guard here: the Confirm button below is disabled
  // by that same predicate, so this never runs without a valid selection.
  const handleContinue = (): void => {
    const selectedTemplate = templates.find(
      (template) => template.id === selectedTemplateId
    );
    onContinue(
      selectedOption as CreateProtocolOption,
      selectedTemplateId ?? undefined,
      selectedTemplate?.name
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      className="create-protocol-modal"
      width={360}
      centered
      closable={false}
      destroyOnHidden
    >
      <div className="create-protocol-modal__container">
        <div className="create-protocol-modal__title">
          <CheckCircleFilled className="create-protocol-modal__title-icon" />
          <span>
            {formatTranslation(t("createProtocolModal.createAProtocol"))}
          </span>
        </div>

        <Radio.Group
          value={selectedOption}
          onChange={(event) => {
            const newOption = event.target.value as CreateProtocolOption;
            setSelectedOption(newOption);
            if (newOption !== "template") {
              setSelectedTemplateId(null);
            }
          }}
          className="create-protocol-modal__options"
        >
          <Radio value="newProtocol">
            {formatTranslation(t("createProtocolModal.startNewProtocol"))}
          </Radio>
          <Radio value="template">
            {formatTranslation(t("createProtocolModal.useTemplate"))}
          </Radio>
        </Radio.Group>

        {selectedOption === "template" && (
          <div className="create-protocol-modal__template-selector">
            {renderTemplateContent()}
          </div>
        )}

        <div className="create-protocol-modal__actions">
          <Button onClick={onCancel}>
            {formatTranslation(t("createProtocol.cancel"))}
          </Button>
          <Button
            type="primary"
            onClick={handleContinue}
            disabled={isConfirmDisabled()}
            className="create-protocol-modal__continue"
          >
            {formatTranslation(t("createProtocolModal.confirm"))}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateProtocolModal;
